import React, { useState } from 'react';
import { X, HelpCircle, Calculator, FileText, CheckCircle, BarChart3, Settings, Download, BookOpen, List, Play, Video } from 'lucide-react';
import { toast } from 'sonner';

/**
 * HelpModal Component
 * Comprehensive help center with FAQs, guides, data dictionary, video tutorials, and table of contents
 */
export default function HelpModal({ show, onClose }) {
  const [activeTab, setActiveTab] = useState('toc'); // 'toc', 'sections', 'dictionary', 'videos'
  const [activeVideo, setActiveVideo] = useState(null);
  
  if (!show) return null;

  // Check if current user is admin
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';

  // Export Purpose & Governance section to formatted text for PowerPoint
  const exportToPowerPoint = () => {
    const governanceSection = helpSections[0]; // First section is Purpose & Governance
    
    let content = '═══════════════════════════════════════════════════════════\n';
    content += `${governanceSection.title.toUpperCase()}\n`;
    content += 'Government Window Pricing Tool - Sales Team Training\n';
    content += '═══════════════════════════════════════════════════════════\n\n';
    
    governanceSection.faqs.forEach((faq, idx) => {
      content += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
      content += `${idx + 1}. ${faq.q}\n`;
      content += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;
      content += `${faq.a}\n\n\n`;
    });
    
    content += '\n═══════════════════════════════════════════════════════════\n';
    content += 'POWERPOINT SLIDE ORGANIZATION GUIDE\n';
    content += '═══════════════════════════════════════════════════════════\n\n';
    content += 'SLIDE 1: Purpose, Workflow & Risk Tiers\n';
    content += '----------------------------------------\n';
    content += '• Extract content from Questions 1, 3, and 5\n';
    content += '• Use the 5 core purposes (Question 1)\n';
    content += '• Use the 8-step workflow (Question 3)\n';
    content += '• Use the 3-tier approval system (Question 5)\n';
    content += '• Focus: Framework and Process\n\n';
    content += 'SLIDE 2: Strategic Value & Best Practices\n';
    content += '----------------------------------------\n';
    content += '• Extract content from Questions 2, 7, and 8\n';
    content += '• Use the 6 critical success factors (Question 2)\n';
    content += '• Use the 5-step negotiation guide (Question 7)\n';
    content += '• Use the 6 strategic benefits (Question 8)\n';
    content += '• Focus: Value and Practical Application\n\n';
    content += '═══════════════════════════════════════════════════════════\n';
    content += `Generated: ${new Date().toLocaleString()}\n`;
    content += '═══════════════════════════════════════════════════════════\n';
    
    // Create and download the file
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Purpose_Governance_PowerPoint_Content_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Purpose & Governance content exported for PowerPoint');
  };

  const helpSections = [
    {
      icon: HelpCircle,
      title: "Purpose & Governance",
      color: "red",
      id: "purpose",
      faqs: [
        {
          q: "What is the strategic purpose of this Pricing Tool?",
          a: "This Pricing Tool serves as the foundation of our financial governance framework and is MANDATORY for all sales and business development activities. Its core purposes are: (1) PROFITABILITY ASSURANCE: Calculate and verify that every proposed deal meets our target net profit margins on a per-service basis before client engagement, (2) RISK MANAGEMENT: Identify deals that require heightened scrutiny due to margin compression, volume sensitivity, or strategic considerations, (3) PRICING CONSISTENCY: Ensure uniform pricing methodology across all territories, services, and client types to maintain market position and internal equity, (4) FINANCIAL TRANSPARENCY: Provide real-time visibility into deal economics including gross fees, merchant costs, reader expenses, sales commissions, partnership incentives, and net profitability, (5) AUDIT & COMPLIANCE: Create a comprehensive paper trail for every client engagement that supports internal reviews, external audits, and regulatory requirements. Every deal must be calculated, approved through proper channels, and documented before any pricing is communicated externally."
        },
        {
          q: "Why is this governance process critical to organizational success?",
          a: "Our governance process protects the financial health and strategic integrity of the organization in multiple ways: (1) PREVENTS MARGIN EROSION: Without centralized oversight, individual deals negotiated below threshold can compound into significant enterprise-wide margin degradation. The tool flags at-risk deals before they impact our bottom line. (2) ENSURES PROFITABILITY STANDARDS: Each service has target margins based on market analysis, cost structure, and strategic objectives. The approval workflow ensures no deal proceeds without meeting these standards or receiving explicit executive authorization with documented justification. (3) MAINTAINS COMPETITIVE POSITIONING: Consistent pricing prevents internal competition where sales teams undercut each other in overlapping territories or service areas. It also ensures we don't leave money on the table by pricing below market rates. (4) CREATES ORGANIZATIONAL ALIGNMENT: When everyone follows the same process, finance can accurately forecast revenue and margins, operations can plan resource allocation, and leadership can make informed strategic decisions. (5) BUILDS INSTITUTIONAL KNOWLEDGE: The system captures deal economics, approval rationale, and performance data that inform future pricing strategy, service expansion decisions, and competitive responses. (6) PROVIDES LEGAL PROTECTION: Complete documentation of pricing decisions, approvals, and calculations protects the company in contract disputes, regulatory inquiries, and financial audits."
        },
        {
          q: "What is the end-to-end deal workflow from calculation to close?",
          a: "Every deal follows this mandatory 8-step workflow to ensure financial discipline and operational excellence: STEP 1 - OPPORTUNITY QUALIFICATION: Gather client requirements including service types needed, estimated transaction volumes, payment types (credit/debit/ACH), current pain points, and decision timeline. STEP 2 - PRICING CALCULATION: Use the Pricing Calculator to model all requested services. Input accurate transaction volumes, payment distributions, reader requirements, partnership structures, and sales commission rates. The tool will calculate gross fees, merchant costs, net fees, implied take rates, gross profit margin (GPM), and net profit margin (NPM) for each service. STEP 3 - MULTI-SERVICE ANALYSIS: If proposing multiple services, use the Service Comparison view to understand percentage contribution of each service to total gross and net fees. Ensure the overall proposal meets profitability standards even if individual services vary. STEP 4 - SAVE & SUBMIT: Save your calculation as a draft, then submit the proposal through the system. The platform will automatically determine approval requirements based on margin thresholds, revenue thresholds, and service-specific rules configured by management. STEP 5 - APPROVAL ROUTING: The system routes your proposal to the appropriate approver (Manager, CFO, or CEO) based on risk level. You'll receive email notifications as your proposal moves through the approval workflow. You can track status in the Approvals section. STEP 6 - CLIENT ENGAGEMENT: ONLY after receiving approval notification, share the SaaS proposal document with proposed rates to the client. Never communicate pricing before approval—this ensures you're operating within approved financial parameters. STEP 7 - NEGOTIATION & ADJUSTMENTS: If the client requests changes, return to the Calculator, adjust parameters, recalculate, and resubmit for approval. Repeat until you reach terms that satisfy both client needs and company profitability standards. STEP 8 - REGISTRATION SUBMISSION: Once the client signs, download/print your approved proposal from the system and submit it with your complete Registration documentation package. Registration requires the approved pricing to verify deal compliance before provisioning services."
        },
        {
          q: "What are the consequences of sharing unapproved pricing with clients?",
          a: "Sharing pricing before receiving approval creates significant risks for both the individual sales professional and the organization: (1) FINANCIAL EXPOSURE: If you quote rates that don't meet margin thresholds, you may lock the company into an unprofitable multi-year contract. This impacts not just your commission, but also company profitability and shareholder value. (2) APPROVAL COMPLICATIONS: If a client expects pricing you quoted informally, you lose negotiating flexibility when the formal approval process identifies margin issues. You may be unable to deliver on your verbal commitments, damaging client relationships. (3) INTERNAL CREDIBILITY: Leadership expects sales professionals to follow established processes. Bypassing the approval workflow undermines trust and may limit your access to strategic opportunities, pricing flexibility on future deals, or advancement opportunities. (4) COMPETITIVE INTELLIGENCE RISK: Unapproved pricing shared externally may leak to competitors, giving them insight into our cost structure, margin targets, and pricing strategy before we're ready to engage in that market. (5) AUDIT & COMPLIANCE GAPS: Every deal must have documented approval before client engagement. Gaps in the approval trail create compliance risks during financial audits, regulatory reviews, and internal controls assessments. (6) PROCESS PRECEDENT: When individuals bypass the process, it encourages others to do the same, eroding the governance framework that protects organizational profitability. All team members are expected to honor the approval workflow as a condition of their sales authority."
        },
        {
          q: "How does the risk-based approval system work?",
          a: "Our approval workflow uses intelligent automation to match deal risk with appropriate approval authority, balancing speed-to-market with financial oversight: TIER 1 - STANDARD DEALS (No Special Approval): These deals meet or exceed all profitability thresholds and proceed directly to client engagement. Criteria: (a) Net Profit Margin ≥ 55% for all services, (b) Annual Gross Fees ≥ $10,000 per service, (c) Standard service configurations without custom terms. These deals require only Account Executive submission—no management approval needed. Process time: Immediate upon submission. TIER 2 - MANAGER APPROVAL REQUIRED (Moderate Risk): These deals show margin compression or revenue concerns requiring management review and explicit authorization. Criteria: (a) Net Profit Margin between 48-55% (approaching minimum acceptable), (b) Annual Gross Fees between $7,000-$10,000 (smaller revenue base), (c) Non-standard payment distributions or service configurations. Manager Review Includes: Validating calculation accuracy, assessing volume assumptions for realism, evaluating strategic rationale (new market entry, competitive displacement, relationship expansion), confirming the deal supports territory/team profitability targets. Typical process time: 24-48 hours. TIER 3 - CFO/CEO APPROVAL REQUIRED (High Risk): These deals require executive review due to significant financial risk or strategic importance. Criteria: (a) Net Profit Margin < 48% (below minimum threshold), (b) Annual Gross Fees < $7,000 (minimal revenue contribution), (c) Strategic deals with long-term implications (major client, new market, competitive response), (d) Custom pricing structures or non-standard terms. Executive Review Includes: Comprehensive financial analysis including break-even scenarios, strategic value assessment beyond immediate profitability, competitive landscape and pricing positioning, long-term client relationship potential and expansion opportunities, impact on company-wide margin targets and financial forecasts. Typical process time: 3-5 business days. SERVICE-SPECIFIC THRESHOLDS: Individual services may have different thresholds based on cost structure and strategic priority. Bail may have different margin requirements than Citations. Check the Settings Manager for current service-specific thresholds (Admin access required to view). APPROVAL NOTIFICATIONS: You'll receive email notifications at each workflow stage: submitted, under review, approved, rejected (with feedback), or sent back for revisions. Track all your proposals in the Approvals section with real-time status updates."
        },
        {
          q: "Why must approved proposals be submitted to Registration?",
          a: "Registration submission serves as the final governance checkpoint ensuring operational and financial integrity before service delivery begins: (1) DEAL VERIFICATION: Registration independently verifies that pricing terms in the signed client contract match the approved proposal from the Pricing Tool. This catches any unauthorized changes made during negotiation and ensures the company delivers services at approved rates. (2) PROFITABILITY CONFIRMATION: By requiring the approved proposal alongside the signed contract, Registration confirms the deal was properly vetted through the approval workflow and meets company profitability standards. This prevents revenue recognition on unapproved or below-threshold deals. (3) PROVISIONING ACCURACY: Registration uses the approved proposal to configure client accounts, set billing rates, allocate reader hardware, and establish merchant processing. Accurate provisioning from the start prevents billing errors, client disputes, and margin leakage. (4) AUDIT TRAIL COMPLETION: The approved proposal becomes part of the permanent client record, creating a complete audit trail from initial calculation through approval, client agreement, and service delivery. This trail supports financial audits, regulatory compliance, and dispute resolution. (5) COMMISSION VALIDATION: Finance uses the approved proposal to validate commission calculations, ensuring sales compensation aligns with approved deal terms and actual margins delivered. This prevents commission overpayments on deals that changed after approval. (6) PERFORMANCE TRACKING: Linking approved proposals to delivered services enables the company to track actual performance vs. projections, refine pricing models, adjust service costs, and improve calculation accuracy over time. SUBMISSION PROCESS: When you're ready to submit your closed deal to Registration, download or print your approved proposal from the Proposals section. Include it in your Registration package along with the signed client contract, service configuration details, reader requirements, and any special terms or conditions. Registration will review everything together to ensure complete alignment before activating the client account."
        },
        {
          q: "How do I handle client requests for below-target pricing?",
          a: "Clients requesting rates that don't meet our margin thresholds present both challenges and opportunities—here's how to navigate these situations strategically: STEP 1 - UNDERSTAND THE 'WHY': Before responding to a low-price request, understand the client's underlying motivation: (a) VOLUME LEVERAGE: They may believe higher volume justifies lower per-transaction rates. Response: Run multiple scenarios showing how volume increases improve margins. Sometimes you can meet their price target if volumes are genuinely higher. (b) COMPETITIVE PRESSURE: They may have received lower quotes from competitors. Response: Emphasize value differentiators (superior support, faster deployment, more reliable technology, easier integration, better reporting) that justify premium pricing. (c) BUDGET CONSTRAINTS: They may have fixed budget limitations. Response: Explore service mix adjustments (fewer services, phased rollout, reduced reader count) that fit their budget while maintaining healthy margins. (d) NEGOTIATION TACTIC: They may be testing your flexibility without real alternatives. Response: Hold firm on pricing while reinforcing value. Many clients will accept higher rates if you demonstrate confidence in your pricing. STEP 2 - OPTIMIZE THE DEAL STRUCTURE: Use the Pricing Calculator to explore margin-friendly adjustments: (a) ADJUST SERVICE MIX: Remove or reduce low-margin services while expanding high-margin services. The Service Comparison table shows percentage contribution—focus on services that drive profitability. (b) MODIFY PAYMENT DISTRIBUTION: Shift more volume to lower-cost payment types (ACH vs. credit card) to reduce merchant fees and improve net margins. (c) REDUCE UPFRONT COSTS: Minimize reader costs through client-purchased hardware, existing infrastructure utilization, or reader-sharing across departments. (d) ELIMINATE PARTNERSHIP INCENTIVES: If the deal includes partnership revenue sharing, evaluate whether removing the partnership improves margins enough to accommodate lower rates. (e) EXTEND COMMITMENT TERM: Longer contracts may justify slightly lower margins due to reduced customer acquisition cost and guaranteed revenue stream. STEP 3 - BUILD THE BUSINESS CASE: If adjustments still don't achieve threshold margins, prepare a CFO/CEO approval request with compelling justification: (a) STRATEGIC VALUE: Is this a marquee client that enhances our credibility in a key market? Does winning this deal create competitive barriers for rivals? Will this client serve as a reference account for larger opportunities? (b) MARKET ENTRY: Is this deal necessary to establish presence in a new geography or service vertical? What's the long-term revenue potential once we're established? Can we raise rates at renewal once we've proven value? (c) RELATIONSHIP EXPANSION: Is this an existing client requesting additional services? Does accepting lower margins on one service protect higher-margin services we already provide? What's the total relationship profitability including all services? (d) COMPETITIVE RESPONSE: Are we at risk of losing a significant client to a competitor? What's the cost of losing this client vs. accepting reduced margins temporarily? Can we negotiate price increases at the next renewal cycle? STEP 4 - SUBMIT FOR EXECUTIVE APPROVAL: Use the Pricing Tool to document the below-threshold deal, include your strategic justification in the submission notes, and route to CFO/CEO for review. Executives will evaluate the total business case and authorize if the strategic value justifies the margin exception. STEP 5 - DECLINE IF NECESSARY: If adjustments don't work and executives don't approve the exception, you may need to decline the opportunity. Remember: Revenue without margin is not growth—it's a liability. Unprofitable deals consume resources, create support costs, and dilute your commission earnings. It's better to focus your time on deals that meet profitability standards and compensate you appropriately for your efforts. Declining strategically also preserves pricing integrity—clients respect firms that hold pricing discipline rather than constantly discounting to win business."
        },
        {
          q: "What role does this tool play in our overall business strategy?",
          a: "The Pricing Tool is not just a calculator—it's a strategic asset that enables sustainable, profitable growth: (1) MARGIN PROTECTION AT SCALE: As the company grows, maintaining margin discipline becomes exponentially more difficult. This tool ensures every deal, regardless of who sells it or where, meets our profitability standards. It's the difference between $50M in revenue at 10% margin vs. $40M in revenue at 25% margin—the latter delivers 2.5x more profit despite lower top-line revenue. (2) DATA-DRIVEN DECISION MAKING: Every calculation, approval, and closed deal generates data that informs strategic decisions. Which services are most profitable? Which markets support premium pricing? What volume thresholds justify reduced rates? Where should we expand next? This tool provides the data foundation for answering these critical questions. (3) SALES ENABLEMENT & EMPOWERMENT: Rather than restricting sales, this tool empowers you with accurate profitability visibility before client engagement. You can negotiate confidently knowing exactly where your margins stand. You can propose creative deal structures and immediately see their financial impact. You can prioritize opportunities that maximize both client value and your commission earnings. (4) COMPETITIVE POSITIONING: Consistent, disciplined pricing builds market credibility. Clients know we don't play pricing games or offer sweetheart deals to favored customers. Our pricing reflects the value we deliver, and our unwillingness to discount signals confidence in that value. This positions us as a premium provider rather than a low-cost commodity vendor. (5) OPERATIONAL EFFICIENCY: When every deal is properly calculated and approved before client engagement, we eliminate costly rework, contract amendments, billing disputes, and margin recovery efforts. Clean deals from the start reduce operational friction and allow the organization to scale efficiently. (6) FINANCIAL FORECASTING ACCURACY: Leadership can forecast revenue and margins with confidence when all deals flow through a standardized calculation and approval process. This accuracy enables better capital allocation, strategic investment decisions, hiring plans, and shareholder communications. By embracing this tool and the governance framework it supports, you're not just following process—you're contributing to the long-term financial health and strategic success of the entire organization."
        }
      ]
    },
    {
      icon: Calculator,
      title: "Calculator & Pricing",
      color: "blue",
      id: "calculator",
      faqs: [
        {
          q: "How do Total Transactions and Total Payments relate?",
          a: "If you enter Total Transactions FIRST, Total Payments will auto-calculate based on your distribution percentages. If you edit Total Payments FIRST, it becomes a manual override and won't auto-update. Click 'Reset to Auto' to re-enable automatic calculation."
        },
        {
          q: "What are take rates and merchant fees?",
          a: "Take rate is the percentage of each transaction you charge as revenue. Merchant fees are the costs paid to payment processors (Stripe, etc.). Your net profit margin is the difference between your take rate and merchant fees."
        },
        {
          q: "Can I save calculations for later?",
          a: "Yes! Click 'Save Draft' to store your calculation. You can return to it anytime from the Calculator History page."
        },
        {
          q: "Why is my Gross Profit Margin (GPM) too low?",
          a: "Low GPM means your take rates are too close to merchant fees. SOLUTION: (1) Increase your proposed take rates, (2) Increase minimum fees, (3) Shift more volume to ACH (lowest cost), or (4) Reduce high-cost payment types like AMEX. TARGET: 50-60% for CC, 70-80% for ACH."
        },
        {
          q: "How do I price competitively while staying profitable?",
          a: "STRATEGY: (1) Use tiered pricing - lower minimums on small transactions ($0-$25) to win deals, higher minimums on large transactions to capture value. (2) Price ACH aggressively (your lowest cost) while keeping CC rates moderate. (3) Check competitor pricing in your market but never go below your costs. (4) Use the Payment Mix to shift clients to lower-cost payment types with incentives."
        },
        {
          q: "What if my calculation shows negative margins?",
          a: "This means you're losing money on every transaction! IMMEDIATE FIXES: (1) Check if merchant fees in Settings are correct (Admin may have set them too high for testing). (2) Increase your take rates - you must charge more than merchant fees. (3) Increase minimum fees to cover low-value transactions. (4) If still negative, this deal is not viable at these rates - adjust pricing or decline the opportunity."
        },
        {
          q: "How accurate should my volume estimates be?",
          a: "Very important! Over-estimating volume makes deals look more profitable than reality. BEST PRACTICES: (1) Request 12 months of historical data from client. (2) Account for seasonality (taxes peak in April, utilities higher in summer/winter). (3) Be conservative - it's better to under-promise and over-deliver. (4) For new services, use industry benchmarks or comparable client data. (5) Add 10-15% buffer for volume fluctuations."
        }
      ]
    },
    {
      icon: FileText,
      title: "Proposals & Collections",
      color: "green",
      id: "proposals",
      faqs: [
        {
          q: "What's the difference between a scenario and a proposal?",
          a: "A scenario is a single service calculation (e.g., Bail service in California). A proposal (collection) bundles multiple scenarios together for a client, allowing you to present a complete pricing package."
        },
        {
          q: "How do I create a multi-service proposal?",
          a: "Navigate to Proposals → New Proposal. Give it a name, then go to the Calculator and add multiple service calculations to that proposal. You can mix different services and states."
        },
        {
          q: "Can I edit a proposal after submission?",
          a: "Proposals in 'Draft' status can be freely edited. Once submitted for approval, only authorized approvers can make changes. If you need to modify a submitted proposal, contact your manager to reopen it."
        }
      ]
    },
    {
      icon: CheckCircle,
      title: "Approval Workflow",
      color: "purple",
      id: "approvals",
      faqs: [
        {
          q: "Why does my proposal require approval?",
          a: "Proposals trigger approval requirements based on: (1) Low gross profit margin (<55% Manager, <48% CFO/CEO), (2) Low gross revenue (<$10K Manager, <$7K CFO/CEO), or (3) High transaction volume. Each service may have different thresholds set by admins."
        },
        {
          q: "What are the approval levels?",
          a: "There are three levels: (1) Standard - no special approval needed, (2) Manager Approval - proposals with moderate risk, (3) CFO/CEO Approval - high-risk or high-value proposals requiring executive review."
        },
        {
          q: "How long does approval take?",
          a: "Approval times vary. Check the 'Avg Approval Time' on the Analytics dashboard for your organization's typical turnaround. You'll receive notifications when your proposal status changes."
        }
      ]
    },
    {
      icon: BarChart3,
      title: "Analytics & Reporting",
      color: "indigo",
      id: "analytics",
      faqs: [
        {
          q: "How do I filter analytics by date range?",
          a: "Use the date filters at the top of the Analytics page. Choose from preset periods (Last 30 Days, Last Quarter, etc.) or select a custom date range. All charts and metrics will update based on your selection."
        },
        {
          q: "What is the Geographic Heatmap?",
          a: "The heatmap shows proposal activity across US states. Darker colors indicate higher revenue or more proposals. Click any state to drill down into city-level data."
        },
        {
          q: "Can I export analytics data?",
          a: "Yes! Click the 'Export' button on the Analytics page. You can export to Excel (with detailed sheets), PDF (formatted report), CSV, or JSON. Choose which columns to include in your export."
        }
      ]
    },
    {
      icon: Settings,
      title: "Settings & Administration",
      color: "slate",
      id: "settings",
      faqs: [
        {
          q: "How do I configure approval thresholds?",
          a: "Admins can navigate to Settings → Approval Thresholds. You can set global defaults or customize thresholds for each service. Changes take effect immediately for new proposals."
        },
        {
          q: "Who can manage services and merchant fees?",
          a: "Only users with Admin role can access Settings to manage services, merchant fees, account executives, and approval thresholds. Contact your system administrator if you need access."
        },
        {
          q: "How do I update my profile?",
          a: "Click your profile icon in the top-right corner → Profile. You can update your name, email, and password. Role changes must be made by an administrator."
        }
      ]
    },
    {
      icon: HelpCircle,
      title: "Troubleshooting & Common Issues",
      color: "orange",
      id: "troubleshooting",
      faqs: [
        {
          q: "My calculation won't save - what's wrong?",
          a: "CHECK THESE COMMON ISSUES: (1) Payment Distribution doesn't equal 100% - adjust percentages to sum exactly to 100. (2) Transaction Tier Distribution doesn't equal 100% - same fix. (3) Required fields are empty - fill in Service, Municipality, and State. (4) Negative margins - system prevents saving unprofitable deals, increase your rates. (5) Browser timeout - if calculation is very old, refresh page and try again."
        },
        {
          q: "Why isn't my scenario showing in Proposals?",
          a: "POSSIBLE REASONS: (1) It's still in Draft - submit it for approval first. (2) It's not added to a Collection - go to Proposals → Add to Collection. (3) Filters are active - check status/date filters on Proposals page. (4) You're an Account Executive viewing another rep's scenarios - scenarios are filtered by user unless you're Admin/Manager. (5) The scenario was soft-deleted - check History for deleted items."
        },
        {
          q: "My approval is taking longer than expected - what should I do?",
          a: "TYPICAL TIMELINES: Manager approval: 24-48 hours | CFO/CEO approval: 3-5 business days. IF DELAYED: (1) Check if approver received notification email (spam folder?). (2) Send follow-up email with scenario name and business justification. (3) Use internal messaging/Slack to remind approver. (4) If urgent, escalate to their manager. (5) Check Approval Queue on Analytics dashboard to see their backlog. AVOID: Submitting duplicate scenarios or constantly resubmitting."
        },
        {
          q: "I submitted my scenario but it went to the wrong approval level - why?",
          a: "Approval levels are AUTOMATICALLY determined by your margins and revenue: NPM ≥55% + Revenue ≥$10K = Standard (no approval). NPM 48-55% OR Revenue $7K-$10K = Manager. NPM <48% OR Revenue <$7K = CFO/CEO. The system calculates this from your inputs. TO CHANGE APPROVAL LEVEL: Adjust your pricing to improve margins or increase transaction volumes to raise revenue. You cannot manually override approval routing - this is by design for financial governance."
        },
        {
          q: "My export is missing data or looks wrong - how do I fix it?",
          a: "COMMON EXPORT ISSUES: (1) Proposed Rates showing 'Not Set' - ensure you filled in proposed pricing section with take rates and minimum fees before saving. (2) Account Code blank - add Account Code in Calculator Scenario Info section. (3) Financials showing $0 - ensure you completed full calculation including volume assumptions and payment distribution. (4) Export won't open in Excel - ensure you have Excel 2010+ or use Google Sheets. (5) Text is cut off - open exported file and enable text wrapping in Excel: Home → Wrap Text."
        },
        {
          q: "Account Code field is not auto-populating - what's wrong?",
          a: "AUTO-POPULATION REQUIREMENTS: (1) Select Municipality from dropdown (don't type it). (2) Select State from dropdown. (3) Select Service Type. (4) Wait 1-2 seconds for system to generate code. MANUAL FIX: If still not populating, the municipality may not be in our 36,000+ entity database. Contact Admin to add it, or enter the Account Code manually if you know it. Account Codes are optional but recommended for government entities."
        },
        {
          q: "I get an error when trying to submit to Registration - why?",
          a: "REGISTRATION REQUIREMENTS: (1) Scenario must be APPROVED first - pending scenarios cannot be submitted. (2) You must generate and download the approved SaaS Agreement PDF. (3) Client must have signed the contract. (4) You need approval documentation (email/system notification). PROCESS: Go to approved scenario → Download SaaS Agreement → Get client signature → Submit signed contract + approval doc to Registration team (separate system/email). If you're missing approval status, contact the approver."
        },
        {
          q: "The calculator is showing different results than my Excel model - why?",
          a: "COMMON DISCREPANCIES: (1) Tier distribution - ensure your Excel uses same transaction distribution as calculator. (2) Rounding differences - calculator rounds to 2 decimals at each step. (3) Merchant fees may be different - check Settings → Merchant Fees (Admin) to see current rates. (4) Reader/SIM costs - verify these match between systems. (5) Commission calculation - ensure both use same base (gross vs net). SOLUTION: Export your scenario and compare line-by-line. If consistent discrepancy, report to Admin for review."
        },
        {
          q: "My proposal was rejected - what are the next steps?",
          a: "REJECTION WORKFLOW: (1) Read the rejection reason carefully - approver provides specific feedback. (2) Common rejection reasons: Margins too low, unrealistic volume estimates, missing strategic justification, pricing below market standards. (3) NEXT ACTIONS: Adjust pricing to improve margins, provide supporting data for volume estimates, add business justification in notes field, or schedule call with approver to discuss. (4) Resubmit once changes are made - scenario returns to Draft status for editing. (5) If you disagree with rejection, escalate to senior management with data supporting your position."
        },
        {
          q: "I need to change an approved scenario - can I edit it?",
          a: "APPROVED SCENARIOS ARE LOCKED to maintain audit integrity. IF YOU NEED CHANGES: (1) MINOR TWEAKS (typos, client name): Contact Admin to make the change. (2) PRICING CHANGES: Clone the approved scenario, make your changes, submit the new version for approval. Mark original as 'Superseded' in notes. (3) AFTER CLIENT SIGNATURE: You cannot change pricing without client agreement. You'll need contract amendment and re-approval. (4) BEFORE SHARING WITH CLIENT: If not yet shared, you can request approver to send back to Draft for revision (loses approval status)."
        }
      ]
    }
  ];

  // Enhanced Data Dictionary with examples and formulas
  const dataDictionary = [
    {
      category: "Financial Metrics",
      icon: "💰",
      terms: [
        { 
          term: "Take Rate", 
          definition: "The percentage of each transaction charged as revenue to the client.",
          example: "2.5% take rate on a $100 transaction = $2.50 fee",
          formula: "Fee = Transaction Amount × Take Rate (%)"
        },
        { 
          term: "Minimum Fee", 
          definition: "The fixed minimum charge per transaction regardless of transaction amount. Ensures profitability on small transactions.",
          example: "With $1.00 min fee and 2.5% rate: A $10 transaction charges $1.00 (min applies), but a $100 transaction charges $2.50 (rate applies)",
          formula: "Actual Fee = MAX(Transaction × Take Rate, Minimum Fee)"
        },
        { 
          term: "Gross Revenue / Annual Gross Fees", 
          definition: "Total fees collected from client before subtracting any costs. This is your top-line revenue.",
          example: "10,000 transactions × $2.50 avg fee = $25,000 gross revenue",
          formula: "Sum of all transaction fees collected"
        },
        { 
          term: "Merchant Fees", 
          definition: "Costs paid to payment processors (Stripe, PayPal, etc.) for processing transactions. These are pass-through costs.",
          example: "Processing $100k in CC transactions at 2.9% + $0.30 = ~$2,900 in merchant fees",
          relatedTerms: ["Interchange Fees", "Net Revenue"]
        },
        { 
          term: "Net Revenue", 
          definition: "Revenue remaining after subtracting merchant fees. This is your actual revenue before other operating costs.",
          example: "$25,000 gross - $8,000 merchant fees = $17,000 net revenue",
          formula: "Gross Revenue - Merchant Fees"
        },
        { 
          term: "Gross Profit Margin (GPM)", 
          definition: "Percentage of revenue remaining after merchant fees. Key indicator of pricing competitiveness vs. processing costs.",
          example: "$17,000 net ÷ $25,000 gross × 100 = 68% GPM",
          formula: "(Net Revenue ÷ Gross Revenue) × 100",
          benchmark: "Target: 50-60% for CC, 70-80% for ACH"
        },
        { 
          term: "Net Profit Margin (NPM)", 
          definition: "Percentage of revenue remaining after ALL costs (merchant fees, readers, commissions, etc.). The ultimate profitability metric.",
          example: "$25,000 gross - $8,000 merchant - $3,000 readers - $2,000 commission = $12,000 net profit = 48% NPM",
          formula: "(Net Fees ÷ Gross Revenue) × 100",
          benchmark: "Target: ≥55% for standard approval, 48-55% needs manager approval, <48% needs CFO/CEO"
        },
        { 
          term: "Implied Take Rate", 
          definition: "Effective percentage charged across all payment types, weighted by transaction volume. Shows blended pricing.",
          example: "If 60% CC at 3% and 40% ACH at 2.5% = 2.8% implied rate",
          formula: "Total Gross Fees ÷ Total Transaction Volume × 100"
        },
        { 
          term: "Contribution Margin", 
          definition: "Revenue minus variable costs; shows profitability contribution of each service to overhead and profit.",
          example: "$25,000 revenue - $10,000 variable costs = $15,000 contribution",
          relatedTerms: ["Net Revenue", "Net Profit Margin"]
        },
        { 
          term: "Annual Recurring Revenue (ARR)", 
          definition: "Predictable revenue expected per year from a client based on their transaction patterns.",
          example: "Client generates $5,000/month in fees = $60,000 ARR",
          formula: "Monthly Average Revenue × 12"
        }
      ]
    },
    {
      category: "Payment Types",
      icon: "💳",
      terms: [
        { 
          term: "ACH (Automated Clearing House)", 
          definition: "Electronic bank-to-bank transfers. Lowest processing costs but slower settlement (2-3 business days).",
          example: "ACH fee: $0.25-$1.00 per transaction regardless of amount",
          useCases: ["Utility bills", "Tax payments", "High-value transactions"],
          pros: "Lowest cost, no card network fees",
          cons: "Slower settlement, requires bank account setup"
        },
        { 
          term: "Credit Card (CC)", 
          definition: "Visa/Mastercard credit transactions. Moderate costs but instant approval and settlement.",
          example: "CC fee: 2.5-3.5% + $0.10-$0.30 per transaction",
          useCases: ["Consumer payments", "Small transactions", "Online payments"],
          pros: "Instant, widely accepted, supports disputes",
          cons: "Higher costs than ACH, subject to chargebacks"
        },
        { 
          term: "AMEX (American Express)", 
          definition: "American Express transactions. Higher costs due to AMEX's premium positioning.",
          example: "AMEX fee: 3-4% + $0.10-$0.30 per transaction",
          useCases: ["Business payments", "Premium services"],
          pros: "Premium customer base, strong fraud protection",
          cons: "Highest processing costs, less widely accepted"
        },
        { 
          term: "Debit Card", 
          definition: "PIN or signature debit cards. Lower costs than credit cards due to direct bank account access.",
          example: "Debit fee: 1.5-2.5% + $0.10-$0.21 per transaction",
          useCases: ["Point-of-sale", "In-person payments", "Lower-income customers"],
          pros: "Lower cost than credit, instant settlement",
          cons: "Requires PIN pad for PIN debit"
        },
        { 
          term: "Interchange Fees", 
          definition: "Fees set by card networks (Visa, Mastercard, AMEX) that payment processors must pay. These vary by card type, industry, and transaction size.",
          example: "Visa rewards card: 2.1% + $0.10 | Debit card: 0.05% + $0.22",
          relatedTerms: ["Merchant Fees"]
        },
        { 
          term: "Card Present vs Card Not Present", 
          definition: "Card present (in-person with card reader) has lower interchange rates than card not present (online/phone) due to lower fraud risk.",
          example: "In-person: 1.8% + $0.10 | Online: 2.5% + $0.10",
          relatedTerms: ["Interchange Fees"]
        }
      ]
    },
    {
      category: "Scenario & Proposal Terms",
      icon: "📊",
      terms: [
        { 
          term: "Scenario", 
          definition: "A single service calculation for a specific client/location. Represents one pricing model.",
          example: "Bail service for Orange County, CA with 5,000 annual transactions",
          relatedTerms: ["Proposal", "Collection"]
        },
        { 
          term: "Proposal / Collection", 
          definition: "A bundle of multiple scenarios presented to a client as a complete pricing package. Allows multi-service deals.",
          example: "Bundle Bail + Traffic + Child Support for Los Angeles County",
          useCases: ["Multi-service clients", "County-wide deals", "Comprehensive packages"]
        },
        { 
          term: "Draft Status", 
          definition: "Scenario saved but not submitted for approval. Can be freely edited, deleted, or cloned.",
          workflow: "Draft → Pending Approval → Approved → Shared with Client"
        },
        { 
          term: "Pending Approval Status", 
          definition: "Scenario submitted and awaiting manager/executive review. Cannot be edited by submitter.",
          actions: "View only, track status, receive notifications"
        },
        { 
          term: "Approved Status", 
          definition: "Scenario has received necessary approvals and can be shared with client. Ready for Registration.",
          actions: "Share with client, download SaaS Agreement, submit to Registration"
        },
        { 
          term: "Rejected Status", 
          definition: "Scenario declined by approver with feedback. Returns to draft for revision.",
          nextSteps: "Review feedback, adjust pricing/volumes, resubmit"
        },
        { 
          term: "Account Code", 
          definition: "Unique identifier combining FIPS code (county) and service code for tracking government entities.",
          example: "GAMU13001841001 = Georgia (GA) + Municipality (MU) + Fulton County (13001) + Service (841001)",
          structure: "STATE(2) + TYPE(2) + FIPS(9) + SERVICE(6)",
          purpose: "Entity tracking, reporting, compliance"
        },
        { 
          term: "FIPS Code", 
          definition: "Federal Information Processing Standards code uniquely identifying US counties and municipalities.",
          example: "13001 = Fulton County, GA | 06037 = Los Angeles County, CA",
          source: "US Census Bureau standard"
        }
      ]
    },
    {
      category: "Approval System",
      icon: "✅",
      terms: [
        { 
          term: "Standard Tier (Auto-Approved)", 
          definition: "Meets profitability standards: NPM ≥55% AND Annual Fees ≥$10K. No special approval required.",
          workflow: "Submit → Instantly Available",
          timeframe: "Immediate",
          useCases: ["Most deals", "Standard pricing", "Healthy margins"]
        },
        { 
          term: "Manager Approval Tier", 
          definition: "Moderate risk: NPM 48-55% OR Annual Fees $7K-$10K. Requires manager review.",
          workflow: "Submit → Manager Review → Approved/Rejected",
          timeframe: "24-48 hours typical",
          reviewCriteria: ["Margin compression", "Volume assumptions", "Strategic value", "Market positioning"]
        },
        { 
          term: "CFO/CEO Approval Tier", 
          definition: "High risk: NPM <48% OR Annual Fees <$7K OR strategic importance. Requires executive authorization.",
          workflow: "Submit → Executive Review → Approved/Rejected",
          timeframe: "3-5 business days typical",
          reviewCriteria: ["Financial analysis", "Strategic value", "Competitive positioning", "Long-term potential", "Impact on company margins"]
        },
        { 
          term: "Approval Threshold", 
          definition: "Financial benchmarks that trigger different approval levels. Configurable per service by admins.",
          example: "Service A: 55% NPM threshold | Service B: 50% NPM threshold (due to higher costs)",
          location: "Settings → Approval Thresholds (Admin only)"
        },
        { 
          term: "Approval Badge", 
          definition: "Visual indicator showing what approval level a scenario requires. Color-coded for quick identification.",
          colors: "Green = Standard | Yellow = Manager | Red = CFO/CEO",
          location: "Calculator results, Scenario cards, Proposals list"
        },
        { 
          term: "Approval History", 
          definition: "Complete timeline of approval workflow showing all reviews, comments, and decisions.",
          includes: ["Submission date/time", "Reviewer actions", "Approval/rejection reasons", "Status changes"],
          purpose: "Audit trail, compliance, learning"
        }
      ]
    },
    {
      category: "Volume & Distribution",
      icon: "📈",
      terms: [
        { 
          term: "Total Annual Volume", 
          definition: "Total dollar amount processed per year across all transactions. Entered in thousands.",
          example: "Enter 1000 for $1,000,000 annual volume",
          calculation: "Auto-calculated if you enter Total Transactions and Avg Transaction Size"
        },
        { 
          term: "Total Transactions", 
          definition: "Number of individual payment transactions per year. Critical for calculating per-transaction fees.",
          example: "50,000 transactions per year = ~4,167 per month = ~137 per day",
          importance: "Drives minimum fee revenue, impacts merchant cost structure"
        },
        { 
          term: "Payment Mix / Distribution", 
          definition: "Percentage breakdown of transactions by payment type. Must sum to exactly 100%.",
          example: "ACH: 14.5% | CC: 62% | AMEX: 8.5% | Debit: 15% = 100%",
          impact: "Different payment types have different costs and profitability",
          validation: "System prevents submission if not equal to 100%"
        },
        { 
          term: "Transaction Tiers", 
          definition: "Dollar amount ranges used to apply tiered pricing. Each tier has its own minimum fee.",
          example: "Tier 1: $0-$25 ($1.00 min) | Tier 2: $26-$50 ($1.25 min) | Tier 3: $51-$100 ($1.50 min)",
          purpose: "Capture value from higher transactions while staying competitive on lower amounts"
        },
        { 
          term: "Tier Distribution", 
          definition: "Percentage of transactions falling into each dollar amount tier. Must sum to 100%.",
          example: "30% under $25 | 40% $25-$50 | 20% $50-$100 | 10% over $100",
          source: "Historical data, industry benchmarks, or client estimates"
        },
        { 
          term: "Average Transaction Size (ATS)", 
          definition: "Mean dollar amount per transaction. Key metric for pricing strategy.",
          example: "$1,000,000 volume ÷ 50,000 transactions = $20 ATS",
          formula: "Total Annual Volume ÷ Total Transactions",
          benchmark: "Utilities: $100-300 | Traffic: $50-150 | Bail: $500-5,000"
        },
        { 
          term: "Transaction Volume Growth", 
          definition: "Expected year-over-year increase in transaction count or dollar volume.",
          example: "Year 1: 50,000 txns | Year 2: 60,000 txns = 20% growth",
          impact: "Affects long-term profitability, may justify lower initial margins"
        }
      ]
    },
    {
      category: "Costs & Expenses",
      icon: "💵",
      terms: [
        { 
          term: "Reader Costs", 
          definition: "Monthly rental or purchase cost of card reader hardware. Amortized over contract term.",
          example: "5 readers × $35/month × 12 months = $2,100 annual cost",
          types: ["Bluetooth mobile readers", "Countertop terminals", "PIN pads", "Wireless 4G readers"],
          relatedTerms: ["SIM Costs"]
        },
        { 
          term: "SIM Costs", 
          definition: "Annual cost for cellular connectivity on wireless card readers. Enables remote payment acceptance.",
          example: "5 wireless readers × $10/month/reader × 12 = $600 annual",
          applicability: "Only for wireless/cellular readers, not Bluetooth or countertop"
        },
        { 
          term: "Sales Commission", 
          definition: "Percentage of gross revenue paid to sales representative. Incentivizes deal closing.",
          example: "5% commission on $25,000 gross = $1,250 annual commission",
          typical: "3-10% depending on deal size and complexity",
          calculation: "Applied to gross revenue, not net"
        },
        { 
          term: "Partnership Incentive", 
          definition: "Revenue share paid to partnership/referral organizations. Common in channel sales.",
          example: "10% partnership share on $25,000 gross = $2,500 annual payment",
          useCases: ["Referral partners", "Technology integrators", "Consulting firms", "Government associations"]
        },
        { 
          term: "Net Fees", 
          definition: "Final profitability after subtracting ALL costs from gross revenue. This is your actual profit.",
          example: "$25,000 gross - $8,000 merchant - $2,100 readers - $1,250 commission - $2,500 partnership = $11,150 net fees",
          formula: "Gross Revenue - Merchant Fees - Reader Costs - SIM Costs - Sales Commission - Partnership Incentive"
        },
        { 
          term: "Fixed Costs", 
          definition: "Costs that don't vary with transaction volume (readers, SIM cards, monthly fees).",
          impact: "Higher volume spreads fixed costs over more transactions, improving margins"
        },
        { 
          term: "Variable Costs", 
          definition: "Costs that increase with transaction volume (merchant fees, commissions on revenue).",
          impact: "Scale linearly with volume, maintain consistent margin percentage"
        }
      ]
    },
    {
      category: "Reports & Exports",
      icon: "📑",
      terms: [
        { 
          term: "Summary Sheet", 
          definition: "Excel/PDF overview showing all scenarios with key metrics in one view. First sheet in exports.",
          columns: ["Scenario Name", "Service", "Account Code", "Status", "Gross Revenue", "Net Revenue", "NPM%", "Approval Level"],
          purpose: "Quick comparison across multiple scenarios"
        },
        { 
          term: "Detail Sheet", 
          definition: "Individual scenario breakdown with complete financial analysis. One sheet per scenario.",
          includes: ["Service info", "Account Code", "Financial summary", "ACH vs CC profitability", "Proposed rates table"],
          purpose: "Deep dive into single scenario economics"
        },
        { 
          term: "Proposed Rates Table", 
          definition: "Export section showing take rates and minimum fees for each payment type.",
          columns: ["Payment Type", "Take Rate (%)", "Minimum Fee ($)"],
          paymentTypes: ["ACH", "CC", "AMEX", "DEBIT"],
          purpose: "Client communication, Registration submission"
        },
        { 
          term: "Profitability Export", 
          definition: "Detailed breakdown of revenue, costs, and margins by service and payment type.",
          metrics: ["Gross fees", "Merchant costs", "Net revenue", "GPM", "All operating costs", "Net fees", "NPM"],
          purpose: "Financial analysis, pricing optimization"
        },
        { 
          term: "Batch Export", 
          definition: "Exporting multiple scenarios simultaneously to Excel or PDF format.",
          limits: "Up to 50 scenarios per export for performance",
          formats: ["Excel (.xlsx)", "PDF (.pdf)"],
          includes: ["Summary sheet + individual detail sheets"]
        },
        { 
          term: "Analytics Dashboard Export", 
          definition: "Customizable export from Analytics page with selected columns and date ranges.",
          customization: ["Choose columns", "Filter by date", "Filter by service/status", "Excel or CSV"],
          useCases: ["Executive reporting", "Pipeline analysis", "Performance tracking"]
        }
      ]
    },
    {
      category: "Service Types",
      icon: "🏛️",
      terms: [
        { 
          term: "Utilities Service", 
          definition: "Water, electric, gas, trash, sewer bill payments.",
          characteristics: "High volume, recurring, moderate transaction sizes ($100-300 average)",
          profitability: "Stable, predictable revenue"
        },
        { 
          term: "Traffic/Citations Service", 
          definition: "Traffic tickets, parking citations, court fines.",
          characteristics: "Variable volume, seasonal patterns, lower transaction sizes ($50-150 average)",
          profitability: "Good margins due to convenience value"
        },
        { 
          term: "Bail Service", 
          definition: "Bail bond payments for court/jail.",
          characteristics: "Lower volume, high transaction sizes ($500-5,000 average), time-sensitive",
          profitability: "High gross revenue per transaction"
        },
        { 
          term: "Child Support Service", 
          definition: "Child support payment processing.",
          characteristics: "Recurring payments, moderate volume, regulated pricing",
          profitability: "Stable but may have regulatory pricing constraints"
        },
        { 
          term: "Tax Service", 
          definition: "Property tax, income tax, and other tax payments.",
          characteristics: "Seasonal (April deadline), high volume spikes, larger transactions",
          profitability: "High volume periods offset seasonal lows"
        }
      ]
    },
    {
      category: "System & Settings",
      icon: "⚙️",
      terms: [
        { 
          term: "Merchant Fee Configuration", 
          definition: "Admin-controlled rates for what the company pays processors. Used in all profitability calculations.",
          location: "Settings → Merchant Fees (Admin only)",
          includes: ["Base rates per payment type", "Interchange fees", "Processor markups"]
        },
        { 
          term: "Service Configuration", 
          definition: "Define available services, their approval thresholds, and default pricing.",
          location: "Settings → Services (Admin only)",
          includes: ["Service name", "Service type", "NPM threshold", "Revenue threshold", "Default rates"]
        },
        { 
          term: "Account Executive Assignment", 
          definition: "Link scenarios to sales reps for commission tracking and pipeline visibility.",
          purpose: "Commission calculation, performance tracking, territory management"
        },
        { 
          term: "Role-Based Access Control", 
          definition: "Different permissions for Preparer, Manager, Admin, CFO, and CEO roles.",
          roles: {
            "Preparer": "Create/edit own scenarios",
            "Manager": "Approve manager-tier proposals",
            "Admin": "Full system configuration",
            "CFO/CEO": "Approve high-risk proposals"
          }
        },
        { 
          term: "Audit Log", 
          definition: "Complete history of all system actions for compliance and troubleshooting.",
          tracks: ["User actions", "Approval decisions", "Configuration changes", "Export activities"],
          retention: "Permanent for compliance"
        }
      ]
    }
  ];

  // Video Tutorial Library
  const videoTutorials = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      duration: '3:30',
      description: 'Navigate the dashboard, understand key sections, and access the calculator for the first time.',
      thumbnail: '🚀',
      level: 'Beginner',
      videoUrl: null, // To be added: YouTube/Vimeo embed URL
      topics: ['Dashboard Overview', 'Navigation', 'First Steps'],
      transcript: '/docs/transcripts/getting-started.txt',
      relatedSections: ['purpose']
    },
    {
      id: 'pricing-calculator',
      title: 'Using the Pricing Calculator',
      duration: '9:30',
      description: 'Complete walkthrough of creating a pricing scenario from start to finish with real examples.',
      thumbnail: '🧮',
      level: 'Beginner',
      videoUrl: null,
      topics: ['Scenario Info', 'Volume Assumptions', 'Tiered Pricing', 'Proposed Rates', 'Results Analysis'],
      transcript: '/docs/transcripts/pricing-calculator.txt',
      relatedSections: ['calculator']
    },
    {
      id: 'proposals-collections',
      title: 'Creating and Managing Proposals',
      duration: '5:45',
      description: 'Bundle multiple scenarios into collections for multi-service deals and client presentations.',
      thumbnail: '📋',
      level: 'Intermediate',
      videoUrl: null,
      topics: ['Creating Collections', 'Adding Scenarios', 'Exporting Proposals', 'Sharing with Clients'],
      transcript: '/docs/transcripts/proposals.txt',
      relatedSections: ['proposals']
    },
    {
      id: 'approval-workflow',
      title: 'Approval Workflow & Status Management',
      duration: '6:30',
      description: 'Understand approval tiers, submission process, and how to review scenarios as an approver.',
      thumbnail: '✅',
      level: 'Intermediate',
      videoUrl: null,
      topics: ['Approval Tiers', 'Submitting', 'Reviewing', 'Notifications'],
      transcript: '/docs/transcripts/approval-workflow.txt',
      relatedSections: ['approvals']
    },
    {
      id: 'data-dictionary',
      title: 'Data Dictionary & Key Terms',
      duration: '4:30',
      description: 'Learn essential financial metrics and terminology used throughout the platform.',
      thumbnail: '📖',
      level: 'Beginner',
      videoUrl: null,
      topics: ['Financial Metrics', 'Payment Types', 'Approval System', 'Account Codes'],
      transcript: '/docs/transcripts/data-dictionary.txt',
      relatedSections: []
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting Common Issues',
      duration: '7:30',
      description: 'Quick solutions to the 10 most common problems users encounter.',
      thumbnail: '🔧',
      level: 'All Levels',
      videoUrl: null,
      topics: ['Save Errors', 'Export Issues', 'Approval Delays', 'Account Codes'],
      transcript: '/docs/transcripts/troubleshooting.txt',
      relatedSections: ['troubleshooting']
    },
    {
      id: 'analytics-reporting',
      title: 'Analytics & Reporting',
      duration: '5:45',
      description: 'Leverage the analytics dashboard, create custom reports, and track key performance indicators.',
      thumbnail: '📊',
      level: 'Advanced',
      videoUrl: null,
      topics: ['Dashboard Overview', 'Geographic Analysis', 'Custom Reports', 'KPI Tracking'],
      transcript: '/docs/transcripts/analytics.txt',
      relatedSections: ['analytics']
    }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <HelpCircle className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Help Center</h2>
                <p className="text-indigo-100 text-sm">Frequently Asked Questions & Guides</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Export Button (Admin Only) */}
              {isAdmin && (
                <button
                  onClick={exportToPowerPoint}
                  className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-sm font-medium"
                  title="Export Purpose & Governance for PowerPoint (Admin Only)"
                >
                  <Download className="w-4 h-4" />
                  Export for PPT
                </button>
              )}
              <button
                onClick={onClose}
                className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Tab Navigation */}
          <div className="sticky top-0 bg-white border-b border-slate-200 px-6 pt-4 pb-2 z-10">
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setActiveTab('toc')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                  activeTab === 'toc'
                    ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-300'
                    : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border-2 border-transparent'
                }`}
              >
                <List className="w-4 h-4" />
                Table of Contents
              </button>
              <button
                onClick={() => setActiveTab('videos')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                  activeTab === 'videos'
                    ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-300'
                    : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border-2 border-transparent'
                }`}
              >
                <Video className="w-4 h-4" />
                Video Tutorials
              </button>
              <button
                onClick={() => setActiveTab('sections')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                  activeTab === 'sections'
                    ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-300'
                    : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border-2 border-transparent'
                }`}
              >
                <HelpCircle className="w-4 h-4" />
                FAQs & Guides
              </button>
              <button
                onClick={() => setActiveTab('dictionary')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                  activeTab === 'dictionary'
                    ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-300'
                    : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border-2 border-transparent'
                }`}
              >
                <BookOpen className="w-4 h-4" />
                Data Dictionary
              </button>
            </div>
          </div>

          <div className="p-6">
            {/* Table of Contents View */}
            {activeTab === 'toc' && (
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border-2 border-indigo-200">
                  <h3 className="text-xl font-bold text-indigo-900 mb-2">📚 Help Center Navigation</h3>
                  <p className="text-slate-600 text-sm mb-4">
                    Jump to any section or explore our comprehensive data dictionary to understand key terms and metrics.
                  </p>
                </div>

                <div className="grid gap-3">
                  {helpSections.map((section, idx) => {
                    const Icon = section.icon;
                    return (
                      <button
                        key={idx}
                        onClick={() => {
                          setActiveTab('sections');
                          setTimeout(() => {
                            document.getElementById(`section-${section.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }, 100);
                        }}
                        className="flex items-center gap-4 p-4 bg-white border-2 border-slate-200 rounded-lg hover:border-indigo-300 hover:shadow-md transition-all text-left group"
                      >
                        <div className={`w-12 h-12 rounded-full bg-${section.color}-100 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                          <Icon className={`w-6 h-6 text-${section.color}-600`} />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-bold text-slate-900 mb-1">{section.title}</h4>
                          <p className="text-xs text-slate-500">{section.faqs.length} questions answered</p>
                        </div>
                        <div className="text-indigo-600 group-hover:translate-x-1 transition-transform">→</div>
                      </button>
                    );
                  })}
                </div>

                <div className="mt-6 space-y-3">
                  <button
                    onClick={() => setActiveTab('videos')}
                    className="w-full flex items-center justify-between gap-4 p-5 bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-lg hover:border-purple-300 hover:shadow-md transition-all group"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Video className="w-6 h-6 text-purple-600" />
                      </div>
                      <div className="text-left">
                        <h4 className="font-bold text-slate-900 mb-1">Video Tutorial Library</h4>
                        <p className="text-xs text-slate-600">{videoTutorials.length} comprehensive video guides (in production)</p>
                      </div>
                    </div>
                    <div className="text-purple-600 group-hover:translate-x-1 transition-transform text-xl">→</div>
                  </button>

                  <button
                    onClick={() => setActiveTab('dictionary')}
                    className="w-full flex items-center justify-between gap-4 p-5 bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-lg hover:border-emerald-300 hover:shadow-md transition-all group"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <BookOpen className="w-6 h-6 text-emerald-600" />
                      </div>
                      <div className="text-left">
                        <h4 className="font-bold text-slate-900 mb-1">Data Dictionary</h4>
                        <p className="text-xs text-slate-600">{dataDictionary.reduce((acc, cat) => acc + cat.terms.length, 0)} terms defined across {dataDictionary.length} categories</p>
                      </div>
                    </div>
                    <div className="text-emerald-600 group-hover:translate-x-1 transition-transform text-xl">→</div>
                  </button>
                </div>
              </div>
            )}

            {/* Video Tutorials View */}
            {activeTab === 'videos' && (
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border-2 border-purple-200">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                      <Video className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-purple-900">Video Tutorial Library</h3>
                      <p className="text-purple-700 text-sm">Step-by-step video guides for every feature</p>
                    </div>
                  </div>
                  <p className="text-slate-600 text-sm">
                    Watch comprehensive video tutorials covering all aspects of the Government Window Pricing Tool.
                    Each video includes timestamps, transcripts, and related help sections for easy reference.
                  </p>
                  
                  {/* Video Production Notice */}
                  <div className="mt-4 bg-white rounded-lg p-4 border border-purple-200">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-xl">🎬</span>
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-900 text-sm mb-1">Videos Coming Soon!</h4>
                        <p className="text-sm text-slate-600">
                          We're currently producing professional video tutorials based on detailed scripts. 
                          In the meantime, detailed written guides are available in the FAQs & Guides section,
                          and the Data Dictionary provides comprehensive term definitions.
                        </p>
                        <p className="text-xs text-slate-500 mt-2">
                          Check back soon or contact your administrator for updates on video availability.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Video Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {videoTutorials.map((video, idx) => (
                    <div
                      key={idx}
                      className={`border-2 rounded-lg overflow-hidden transition-all ${
                        video.videoUrl
                          ? 'border-slate-200 hover:border-purple-300 hover:shadow-lg cursor-pointer'
                          : 'border-slate-200 bg-slate-50'
                      }`}
                      onClick={() => video.videoUrl && setActiveVideo(video)}
                    >
                      {/* Video Thumbnail */}
                      <div className="relative bg-gradient-to-br from-purple-100 to-pink-100 h-40 flex items-center justify-center">
                        <div className="text-6xl">{video.thumbnail}</div>
                        {video.videoUrl ? (
                          <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                            <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center">
                              <Play className="w-8 h-8 text-purple-600 ml-1" />
                            </div>
                          </div>
                        ) : (
                          <div className="absolute top-2 right-2 bg-amber-400 text-amber-900 px-2 py-1 rounded text-xs font-bold">
                            Coming Soon
                          </div>
                        )}
                        <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded text-xs font-medium">
                          {video.duration}
                        </div>
                      </div>

                      {/* Video Info */}
                      <div className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-bold text-slate-900 text-base">{video.title}</h4>
                          <span className={`text-xs px-2 py-1 rounded font-medium ${
                            video.level === 'Beginner' ? 'bg-green-100 text-green-700' :
                            video.level === 'Intermediate' ? 'bg-blue-100 text-blue-700' :
                            video.level === 'Advanced' ? 'bg-purple-100 text-purple-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {video.level}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 mb-3 line-clamp-2">
                          {video.description}
                        </p>

                        {/* Topics Covered */}
                        <div className="space-y-2">
                          <p className="text-xs font-semibold text-slate-700">Topics Covered:</p>
                          <div className="flex flex-wrap gap-1">
                            {video.topics.slice(0, 3).map((topic, topicIdx) => (
                              <span
                                key={topicIdx}
                                className="inline-flex items-center px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 text-xs"
                              >
                                {topic}
                              </span>
                            ))}
                            {video.topics.length > 3 && (
                              <span className="text-xs text-slate-500">
                                +{video.topics.length - 3} more
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Action Button */}
                        {video.videoUrl ? (
                          <button
                            onClick={() => setActiveVideo(video)}
                            className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium text-sm"
                          >
                            <Play className="w-4 h-4" />
                            Watch Now
                          </button>
                        ) : (
                          <div className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 bg-slate-200 text-slate-500 rounded-lg font-medium text-sm cursor-not-allowed">
                            <Video className="w-4 h-4" />
                            In Production
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Video Scripts Available */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-blue-900 mb-1">Detailed Video Scripts Available</h4>
                      <p className="text-sm text-blue-800 mb-3">
                        Complete scripts and storyboards for all 7 video tutorials are documented and ready for production.
                        Each script includes timing, voiceover text, screen actions, and production notes.
                      </p>
                      <a
                        href="/VIDEO_TUTORIAL_SCRIPTS.md"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-medium text-blue-700 hover:text-blue-900 hover:underline"
                      >
                        <Download className="w-4 h-4" />
                        View Video Scripts Document
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* FAQ Sections View */}
            {activeTab === 'sections' && (
              <div className="space-y-6">
                {helpSections.map((section, idx) => (
                  <div key={idx} id={`section-${section.id}`} className="border border-slate-200 rounded-lg overflow-hidden scroll-mt-4">
                    {/* Section Header */}
                    <div className={`bg-${section.color}-50 border-b border-${section.color}-200 p-4`}>
                      <div className="flex items-center gap-3">
                        <section.icon className={`w-6 h-6 text-${section.color}-600`} />
                        <h3 className={`font-bold text-${section.color}-900 text-lg`}>{section.title}</h3>
                      </div>
                    </div>

                    {/* FAQs */}
                    <div className="p-4 space-y-4">
                      {section.faqs.map((faq, faqIdx) => (
                        <div key={faqIdx} className="space-y-2">
                          <div className="flex items-start gap-2">
                            <span className={`text-${section.color}-600 font-bold text-sm mt-0.5`}>Q:</span>
                            <p className="font-semibold text-slate-900 text-sm flex-1">{faq.q}</p>
                          </div>
                          <div className="flex items-start gap-2 pl-6">
                            <span className="text-slate-400 font-bold text-sm mt-0.5">A:</span>
                            <p className="text-slate-600 text-sm leading-relaxed flex-1">{faq.a}</p>
                          </div>
                          {faqIdx < section.faqs.length - 1 && (
                            <div className="border-b border-slate-100 pt-2"></div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Data Dictionary View */}
            {activeTab === 'dictionary' && (
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-6 border-2 border-emerald-200">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                      <BookOpen className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-emerald-900">Data Dictionary</h3>
                      <p className="text-emerald-700 text-sm">Comprehensive definitions of key terms and metrics</p>
                    </div>
                  </div>
                  <p className="text-slate-600 text-sm">
                    Understanding the terminology is essential for accurate pricing calculations and proposal creation.
                    Use this dictionary as a quick reference guide for any unfamiliar terms. Each term includes
                    definitions, examples, formulas, and benchmarks where applicable.
                  </p>
                </div>

                {dataDictionary.map((category, catIdx) => (
                  <div key={catIdx} className="border border-slate-200 rounded-lg overflow-hidden">
                    <div className="bg-gradient-to-r from-slate-100 to-slate-50 border-b border-slate-200 px-4 py-3">
                      <h4 className="font-bold text-slate-900 text-base flex items-center gap-2">
                        <span className="text-xl">{category.icon}</span>
                        {category.category}
                        <span className="ml-auto text-xs font-normal text-slate-500">
                          {category.terms.length} terms
                        </span>
                      </h4>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {category.terms.map((item, termIdx) => (
                        <div key={termIdx} className="p-4 hover:bg-slate-50 transition-colors">
                          <div className="space-y-2">
                            {/* Term and Definition */}
                            <div>
                              <h5 className="font-bold text-slate-900 text-base mb-1 flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                                {item.term}
                              </h5>
                              <p className="text-slate-700 text-sm leading-relaxed pl-3.5">
                                {item.definition}
                              </p>
                            </div>

                            {/* Example */}
                            {item.example && (
                              <div className="pl-3.5 mt-2">
                                <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded-r">
                                  <p className="text-xs font-semibold text-blue-900 mb-1">💡 Example:</p>
                                  <p className="text-sm text-blue-800">{item.example}</p>
                                </div>
                              </div>
                            )}

                            {/* Formula */}
                            {item.formula && (
                              <div className="pl-3.5 mt-2">
                                <div className="bg-purple-50 border-l-4 border-purple-400 p-3 rounded-r">
                                  <p className="text-xs font-semibold text-purple-900 mb-1">📐 Formula:</p>
                                  <p className="text-sm font-mono text-purple-800">{item.formula}</p>
                                </div>
                              </div>
                            )}

                            {/* Benchmark */}
                            {item.benchmark && (
                              <div className="pl-3.5 mt-2">
                                <div className="bg-amber-50 border-l-4 border-amber-400 p-3 rounded-r">
                                  <p className="text-xs font-semibold text-amber-900 mb-1">🎯 Benchmark:</p>
                                  <p className="text-sm text-amber-800">{item.benchmark}</p>
                                </div>
                              </div>
                            )}

                            {/* Use Cases */}
                            {item.useCases && (
                              <div className="pl-3.5 mt-2">
                                <div className="bg-green-50 border-l-4 border-green-400 p-3 rounded-r">
                                  <p className="text-xs font-semibold text-green-900 mb-1">✓ Common Use Cases:</p>
                                  <ul className="text-sm text-green-800 space-y-1">
                                    {item.useCases.map((useCase, idx) => (
                                      <li key={idx}>• {useCase}</li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                            )}

                            {/* Pros & Cons */}
                            {(item.pros || item.cons) && (
                              <div className="pl-3.5 mt-2 grid grid-cols-2 gap-2">
                                {item.pros && (
                                  <div className="bg-green-50 border border-green-200 p-2 rounded">
                                    <p className="text-xs font-semibold text-green-900 mb-1">✓ Pros:</p>
                                    <p className="text-xs text-green-700">{item.pros}</p>
                                  </div>
                                )}
                                {item.cons && (
                                  <div className="bg-red-50 border border-red-200 p-2 rounded">
                                    <p className="text-xs font-semibold text-red-900 mb-1">✗ Cons:</p>
                                    <p className="text-xs text-red-700">{item.cons}</p>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Related Terms */}
                            {item.relatedTerms && (
                              <div className="pl-3.5 mt-2">
                                <p className="text-xs text-slate-500 flex items-center gap-2">
                                  <span className="font-semibold">🔗 Related:</span>
                                  {item.relatedTerms.map((rel, idx) => (
                                    <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                                      {rel}
                                    </span>
                                  ))}
                                </p>
                              </div>
                            )}

                            {/* Additional Fields */}
                            {item.workflow && (
                              <div className="pl-3.5 mt-2">
                                <p className="text-xs text-slate-600">
                                  <span className="font-semibold">Workflow:</span> {item.workflow}
                                </p>
                              </div>
                            )}
                            {item.timeframe && (
                              <div className="pl-3.5 mt-1">
                                <p className="text-xs text-slate-600">
                                  <span className="font-semibold">Timeframe:</span> {item.timeframe}
                                </p>
                              </div>
                            )}
                            {item.structure && (
                              <div className="pl-3.5 mt-1">
                                <p className="text-xs text-slate-600 font-mono">
                                  <span className="font-semibold">Structure:</span> {item.structure}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="text-center py-6 border-t border-slate-200 px-6">
            <p className="text-slate-600 text-sm">
              Need more help? Contact your system administrator or{' '}
              <a href="mailto:support@governmentwindow.com" className="text-indigo-600 font-semibold hover:underline">
                email support
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
